import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { chatMarkdownComponents } from "@/lib/markdownHelpers";
import AgentChatRenderer, { specNeedsContentFallback } from "./agentChat/AgentChatRenderer";
import { extractToolResults, hasLegacyM360Fences, parseAgentMessage } from "./agentChat/parseAgentMessage";
import { mergeProfilePhotosIntoBlock, mergeProfilePhotosIntoSpec } from "./agentChat/profilePhotoFromToolResults";
import {
  hasRenderableStructuredBlock,
  mergeSpecLinksIntoPromotedBlock,
  shouldPreferEventCardsOverSpec,
  shouldPreferToolResultEventsOverSpec
} from "./agentChat/specOverrides";
import M360StructuredBlockView from "./M360StructuredBlock";
import {
  parseChatContentSegments,
  promoteAgentProseToStructuredBlock,
  sanitizeToolResultsMarkdown
} from "./m360StructuredContent";

type ChatRichContentProps = {
  text: string;
};

function JsonPanel({ value }: { value: unknown }) {
  const formatted = JSON.stringify(value, null, 2);

  return (
    <pre className="overflow-x-auto rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-low p-3 text-body-small text-sys-brand-on-surface">
      <code>{formatted}</code>
    </pre>
  );
}

function LegacyStructuredContent({ text }: { text: string }) {
  const segments = parseChatContentSegments(text);

  return (
    <div className="space-y-3">
      {segments.map((segment, index) => {
        switch (segment.kind) {
          case "structured":
            return <M360StructuredBlockView key={`structured-${index}`} block={segment.value} />;
          case "json":
            return <JsonPanel key={`json-${index}`} value={segment.value} />;
          case "markdown":
            return (
              <ReactMarkdown key={`md-${index}`} remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
                {segment.value}
              </ReactMarkdown>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

export default function ChatRichContent({ text }: ChatRichContentProps) {
  const { text: messageBody, toolResults } = extractToolResults(text);
  const { prose, spec } = parseAgentMessage(messageBody);
  const useLegacy = !spec && hasLegacyM360Fences(messageBody);

  if (useLegacy) {
    const legacySource = [messageBody, toolResults].filter(Boolean).join("\n\n");
    return <LegacyStructuredContent text={legacySource} />;
  }

  const fallbackSource = [prose, toolResults].filter(Boolean).join("\n\n");
  const promotedBlock = fallbackSource ? promoteAgentProseToStructuredBlock(fallbackSource) : null;
  const renderablePromoted = hasRenderableStructuredBlock(promotedBlock) ? promotedBlock : null;
  const mergedPromotedBlock = mergeProfilePhotosIntoBlock(
    mergeSpecLinksIntoPromotedBlock(spec, renderablePromoted),
    toolResults
  );
  const mergedSpec = mergeProfilePhotosIntoSpec(spec, toolResults);
  const replaceSpecWithEvents = Boolean(
    mergedPromotedBlock && mergedSpec && shouldPreferEventCardsOverSpec(mergedSpec, mergedPromotedBlock)
  );
  const replaceSpecWithToolResultEvents = Boolean(
    mergedPromotedBlock && mergedSpec && shouldPreferToolResultEventsOverSpec(mergedSpec, mergedPromotedBlock)
  );
  const specIsBroken = Boolean(mergedSpec && specNeedsContentFallback(mergedSpec));
  const shouldFallbackToPromoted = Boolean(
    mergedPromotedBlock && (!mergedSpec || specIsBroken || replaceSpecWithEvents || replaceSpecWithToolResultEvents)
  );
  const proseFallbackBlock = shouldFallbackToPromoted ? mergedPromotedBlock : null;
  const showProse = Boolean(prose) && !proseFallbackBlock;
  const showSpec = Boolean(mergedSpec) && !proseFallbackBlock && !specIsBroken;
  const sanitizedToolResults = toolResults ? sanitizeToolResultsMarkdown(toolResults) : null;
  const showToolResultsMarkdown = Boolean(sanitizedToolResults && !proseFallbackBlock && !showSpec);

  return (
    <div className="space-y-3">
      {showProse ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
          {prose}
        </ReactMarkdown>
      ) : null}
      {proseFallbackBlock ? <M360StructuredBlockView block={proseFallbackBlock} /> : null}
      {showSpec ? <AgentChatRenderer spec={mergedSpec} /> : null}
      {showToolResultsMarkdown ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
          {sanitizedToolResults}
        </ReactMarkdown>
      ) : null}
    </div>
  );
}
